import React, { createContext, useContext, useMemo, type ReactNode } from 'react';
import { TourSpotlighTargetName } from './TourSpotlightTarget';

interface TourSpotlightContextValue {
	activeTarget: TourSpotlighTargetName | null;
	content: ReactNode | null;
}

export const TourSpotlightContext: React.Context<TourSpotlightContextValue | null> =
	createContext<TourSpotlightContextValue | null>(null);

export const TourSpotlightProvider = ({
	children,
	activeTarget,
	content,
}: {
	children: ReactNode;
	activeTarget: TourSpotlighTargetName | null;
	content: ReactNode | null;
}): React.JSX.Element => {
	// TourManagers can be nested (e.g. the context-menu tour wraps the onboarding
	// tour). A nested provider would otherwise REPLACE the parent value outright,
	// so an inner manager that is idle (activeTarget === null) would shadow an
	// outer manager that has an active spotlight, and no TourSpotlightTarget
	// beneath it could ever match. Fall through to the parent when this provider
	// has nothing to show, so the innermost *active* tour wins instead of simply
	// the innermost tour.
	const parent = useContext(TourSpotlightContext);

	const value = useMemo(
		() => (activeTarget !== null ? { activeTarget, content } : parent),
		[activeTarget, content, parent],
	);

	return <TourSpotlightContext.Provider value={value}>{children}</TourSpotlightContext.Provider>;
};
